import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AsOfDate from '../../../core/components/AsOfDate';
import DataTable from '../../../core/components/DataTable';
import * as utils from '../../../core/utils';
import { useAppContext } from '../../../providers/AppProvider';
import { selectSiteName } from '../../settings/reducer';
import { setFromDate, setUntilDate } from '../../tasks/actions';
import { Interval, TaskCompletion } from '../../tasks/models';
import { selectFromDate, selectUntilDate } from '../../tasks/reducer';
import { TaskCompletionStatus } from '../../tasks/types';
import { useGetTaskCompletionsForTaskQuery } from '../api';
import { selectSite } from '../../../store/authReducer';

const PAGE_SIZE = 10;

const History = (props) => {
  const { task } = props;
  const { timeZone } = useAppContext();
  const [page, setPage] = useState(0);

  const fromDate = useSelector(selectFromDate);
  const untilDate = useSelector(selectUntilDate);
  const siteName = useSelector(selectSiteName);
  const siteId = useSelector(selectSite);
  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    setPage(0);
  }, [fromDate, untilDate, task.id]);

  const { data } = useGetTaskCompletionsForTaskQuery(
    {
      taskId: task.id,
      siteId,
      page,
      pageSize: PAGE_SIZE,
      fromDate: fromDate.format('YYYY-MM-DD'),
      untilDate: untilDate.format('YYYY-MM-DD'),
    },
    { skip: !task.id }
  );

  const taskCompletions = (data?.results || []).map((t) => new TaskCompletion(t));
  const total = data?.total || 0;
  const currentPage = data?.page || 0;
  const pageSize = data?.pageSize || PAGE_SIZE;

  const getDue = useCallback(
    (taskCompletion) => {
      if (task.interval === Interval.CarCount) {
        return taskCompletion.scheduleCarCount;
      }
      if (taskCompletion.scheduleEnd) {
        if (task.interval === Interval.Hourly) {
          return moment.tz(taskCompletion.scheduleEnd, timeZone).format('MM/DD/YYYY hh:mm a');
        }
        return moment.tz(taskCompletion.scheduleEnd, timeZone).format('MM/DD/YYYY');
      }
      return intl.formatMessage({ id: 'notAvailable' });
    },
    [task.interval, intl, timeZone]
  );

  const initializeDatatable = useCallback(
    (taskCompletionsIntl) => {
      const tableData = taskCompletionsIntl.map((taskCompletion) => {
        const employee = taskCompletion.employee;
        const due = getDue(taskCompletion);
        const late =
          moment().utc().isAfter(moment(taskCompletion.scheduleEnd).utc()) &&
          utils.toEnum(TaskCompletionStatus, taskCompletion.status) ===
            TaskCompletionStatus.Pending;
        const rowData = {
          _id: taskCompletion.id,
          due,
          status: late
            ? intl.formatMessage({ id: 'expired' })
            : taskCompletion.status == 'Snoozed'
            ? intl.formatMessage({ id: 'blocked' })
            : taskCompletion.status,
          timestamp:
            taskCompletion.status !== 'Pending'
              ? moment.tz(taskCompletion.timestamp, timeZone).format('MM/DD/YYYY h:mm:ss a')
              : '',
          carCount: taskCompletion.carCount,
          employee: employee ? `${employee.firstName} ${employee.lastName}` : '',
          notes: taskCompletion.notes,
        };
        if (task.interval !== Interval.CarCount) {
          delete rowData.carCount;
        }
        return rowData;
      });

      const headers = [
        { name: intl.formatMessage({ id: 'due' }) },
        { name: intl.formatMessage({ id: 'status' }) },
        { name: intl.formatMessage({ id: 'updated' }) },
        { name: intl.formatMessage({ id: 'carCount' }) },
        { name: intl.formatMessage({ id: 'teamMember' }) },
        { name: intl.formatMessage({ id: 'notes' }) },
      ];
      if (task.interval !== Interval.CarCount) {
        headers.splice(3, 1);
      }

      return { data: tableData, headers };
    },
    [getDue, intl, task.interval, timeZone]
  );

  const getPaginationInfo = useCallback(
    () => ({
      total,
      page: currentPage,
      pageSize,
      goToPage: (newPage) => setPage(newPage),
    }),
    [total, currentPage, pageSize]
  );

  return (
    <div className="task-history">
      <div className="as-of-date-container">
        <AsOfDate
          fromDate={fromDate}
          untilDate={untilDate}
          setFromDate={(date) => dispatch(setFromDate(date))}
          setUntilDate={(date) => dispatch(setUntilDate(date))}
          fetchWithQuery={() => setPage(0)}
          title={`${intl.formatMessage({ id: 'tasks' })}: `}
          dateTarget="history"
          intl={intl}
        />
      </div>
      {task.isEssentialTask && (
        <div className="mt-2">{`${intl.formatMessage({ id: 'site' })}: ${siteName}`}</div>
      )}
      <DataTable paginationInfo={getPaginationInfo()} {...initializeDatatable(taskCompletions)} />
    </div>
  );
};

export default History;
